
import { motion, useTransform, type MotionValue } from "framer-motion";
import { DRIFT_NODE_ID, SNAP_FADE } from "../keyframe-data";
import type { KeyframeBand } from "../types";
import {
  LAYOUT,
  EDGE,
  DIM_OPACITY,
  polylinePath,
  getNode,
} from "../graph-layout";
import { NodeCard } from "../elements/node-card";
import { TierLanes } from "../elements/tier-lanes";
import { WorkloadZone } from "../elements/workload-zone";

interface Props {
  progress: MotionValue<number>;
  /** Beat 5 band. */
  band: KeyframeBand;
}

/** Helper: at beat 5, non-drift elements dim once we cross the band's mid-point. */
export function useBeat5DimOpacity(progress: MotionValue<number>, band: KeyframeBand) {
  const mid = band.start + (band.end - band.start) / 2;
  return useTransform(progress, [band.start, mid, band.end], [1, 1, DIM_OPACITY]);
}


export function Beat5Drift({ progress, band }: Props) {
  // Snap-style transitions — short fade windows so each state reads as discrete.
  const FADE = SNAP_FADE;
  // Phase 1 — drift zone + bedrock + drift edge: enter snappily at beat 5 start, persist.
  const phase1 = useTransform(
    progress,
    [band.start, band.start + FADE],
    [0, 1],
  );
  const bedrock = getNode(DRIFT_NODE_ID);

  // Brief scale-pop on bedrock arrival — overshoots slightly then settles.
  // Anchored at the card's center so the box stays in place.
  const bedrockPop = useTransform(
    progress,
    [band.start, band.start + FADE * 0.55, band.start + FADE * 1.4],
    [0.88, 1.05, 1],
  );
  const bedrockTransform = useTransform(
    bedrockPop,
    (s) => `translate(${bedrock.cx} ${bedrock.cy}) scale(${s}) translate(${-bedrock.cx} ${-bedrock.cy})`,
  );

  // Drift edge polyline from lambda to bedrock — pulled from LAYOUT.
  const driftEdgeData = LAYOUT.edges.find((e) => e.from === "lambda" && e.to === DRIFT_NODE_ID);
  const driftEdgePath = driftEdgeData ? polylinePath(driftEdgeData.points) : "";

  return (
    <motion.g data-beat="5" aria-hidden="true">
      {/* Phase 1: AI/ML lane materializes + drift edge + Bedrock node + expanded workload zone */}
      <motion.g style={{ opacity: phase1 }}>
        <TierLanes showAiml={true} aimlOpacity={1} />

        {/* Workload zone expands to include Bedrock — overlays the base zone from beat 4. */}
        <WorkloadZone includeDrift />

        {/* Drift edge (lambda → bedrock) — red dashed */}
        <path
          d={driftEdgePath}
          fill="none"
          stroke={EDGE.driftStroke}
          strokeWidth={EDGE.flowLitWidth}
          strokeDasharray={EDGE.driftDash}
          strokeLinejoin="round"
          strokeLinecap="round"
          markerEnd="url(#arrow-drift)"
          className="edge-march"
        />

        {/* Bedrock node card — drift state (red across stroke + icon + text), pops in. */}
        <motion.g style={{ transform: bedrockTransform }}>
          <NodeCard
            id="bedrock"
            x={bedrock.x}
            y={bedrock.y}
            w={bedrock.w}
            state="drift"
          />
        </motion.g>
      </motion.g>
    </motion.g>
  );
}
